import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { isNil } from 'lodash';
import { CssBaseline } from '@mui/material';

import { RootLayout } from '#packages/web/foundations/layouts/root-layout';

import { createModuleLogger } from '#apps/client-web/app/utils/logger';
import { InitPage } from '#apps/client-web/app/pages/init';
import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { AppErrorBoundary } from '#apps/client-web/app/components/app-error-boundary';
import { App } from '#apps/client-web/app';

import sentry from './bootstrap/sentry';
import mockServiceWorker from './bootstrap/mock-service-worker';

/**
 * Main entry point of the application.
 * The goal of this function is to initialize the app and render it to the DOM.
 * It performs necessary asynchronous setup before rendering the main App component.
 * It also handles errors during initialization and displays appropriate error UI.
 * PS: for performance (memory) reasons, the whole app is wrapped inside this function.
 * This ensures that any variables defined here are eligible for garbage collection
 * once the initialization is complete and the app is rendered.
 */
(() => {
  const logger = createModuleLogger('main');

  // find the root element & create the React root
  const rootElement = (() => {
    let rootElement = document.getElementById('root');

    if (isNil(rootElement)) {
      logger.error('Root element with id "root" not found in the document');

      // Create the root element if it does not exist
      rootElement = document.createElement('div');
      rootElement.id = 'root';
      document.body.appendChild(rootElement);

      logger.info('Created root element with id "root" and appended to body');
    }

    return rootElement;
  })();
  const root = createRoot(rootElement);

  const renderApp = (node: ReactNode) =>
    root.render(
      <StrictMode>
        <CssBaseline />
        <RootLayout>
          <AppErrorBoundary>{node}</AppErrorBoundary>
        </RootLayout>
      </StrictMode>,
    );

  // Initial render to show something while the app is initializing
  renderApp(<InitPage />);

  (async function init() {
    // Perform any necessary asynchronous initialization here
    logger.debug('Starting app initialization...');

    await sentry.setup();
    await mockServiceWorker.setup();

    logger.debug('App initialization completed');
  })()
    .then(() => {
      logger.info('App initialization successful, rendering app...');
      renderApp(<App />);
    })
    .catch((err) => {
      logger.error('App initialization failed', err);
      renderApp(<ThrowError error={err} />);
    })
    .finally(() => {
      logger.info('App initialization process finished');
    });
})();
